import React from "react";
import moment from "moment-timezone";
import {
  getMembershipFriendlyName,
  getPackageFriendlyName,
} from "../../utils/membershipUtils";
import "./CreditSummaryTable.css";

// Session Credits tab: the full credit history from the customer record.
// Every bucket with any activity is listed, including used-up packages,
// expired credits, cancelled memberships, and the completed counts that the
// Overview table leaves out. "Booked" here is the stored counter.

const has = (value) => value && String(value).toLowerCase() !== "none";
const num = (value) => Number(value) || 0;
const formatDate = (value) =>
  value ? moment.utc(value).tz("America/Phoenix").format("MMM D, YYYY") : "";

const isActiveStatus = (status) => status === "active" || status === "trialing";

const hasActivity = (usage) =>
  Boolean(usage) &&
  (num(usage.remaining) !== 0 ||
    num(usage.booked) !== 0 ||
    num(usage.completed) !== 0 ||
    num(usage.totalCompleted) !== 0 ||
    num(usage.forfeited) !== 0 ||
    num(usage.totalSessions) !== 0);

// Pure: the history rows for a customer DTO. Exported for tests.
export function buildCreditHistory(customer, now = new Date()) {
  if (!customer) return [];
  const rows = [];
  const add = (key, name, type, usage, status, dateLabel = "") => {
    if (!hasActivity(usage)) return;
    rows.push({
      key,
      name,
      type,
      status,
      left: num(usage.remaining),
      booked: num(usage.booked),
      completed: num(usage.completed),
      totalCompleted: num(usage.totalCompleted),
      forfeited: num(usage.forfeited),
      dateLabel,
    });
  };
  const expired = (date) => Boolean(date) && new Date(date) < now;
  const usedUp = (usage) => num(usage?.remaining) <= 0;
  const oneTimeStatus = (usage, expiresAt) =>
    expired(expiresAt) ? "Expired" : usedUp(usage) ? "Used up" : "Active";
  const expiryLabel = (date) =>
    date ? `${expired(date) ? "Expired" : "Expires"} ${formatDate(date)}` : "No expiry";

  // Memberships
  const paused = ["paused", "scheduled_pause"].includes(customer.membershipAccessStatus);
  const legacyMembership = (key, typeField, statusField, periodEndField, fallback) => {
    const code = customer[typeField];
    const active = has(code) && isActiveStatus(customer[statusField]);
    add(
      key,
      has(code) ? getMembershipFriendlyName(code) : fallback,
      "Membership",
      customer[key],
      active ? (paused ? "Paused" : "Active") : "Cancelled",
      customer[periodEndField] ? `Period ends ${formatDate(customer[periodEndField])}` : ""
    );
  };
  legacyMembership(
    "saunaMembershipSessionUsage",
    "saunaMembershipType",
    "saunaMembershipStatus",
    "saunaMembershipCurrentPeriodEnd",
    "Sauna Membership"
  );
  legacyMembership(
    "redLightMembershipSessionUsage",
    "redLightMembershipType",
    "redLightMembershipStatus",
    "redLightMembershipCurrentPeriodEnd",
    "Red Light Membership"
  );
  {
    const end = customer.sharedMembershipCurrentPeriodEnd;
    const active = has(customer.sharedMembershipName) && end && !expired(end);
    add(
      "sharedMembershipSessionUsage",
      has(customer.sharedMembershipName) ? customer.sharedMembershipName : "Shared Membership",
      "Membership",
      customer.sharedMembershipSessionUsage,
      active ? (paused ? "Paused" : "Active") : "Ended",
      end ? `Period ends ${formatDate(end)}` : ""
    );
  }

  // Packages
  [
    ["saunaPackageSessionUsage", "saunaPackageName", "saunaPackageExpiresAt", "Sauna Package"],
    ["redLightPackageSessionUsage", "redLightPackageName", "redLightPackageExpiresAt", "Red Light Package"],
    ["sharedPackageSessionUsage", "sharedPackageName", "sharedPackageExpiresAt", "Shared Package"],
  ].forEach(([key, nameField, expiryField, fallback]) => {
    const usage = customer[key];
    add(
      key,
      has(customer[nameField]) ? getPackageFriendlyName(customer[nameField]) : fallback,
      "Package",
      usage,
      oneTimeStatus(usage, customer[expiryField]),
      expiryLabel(customer[expiryField])
    );
  });

  // Drop-ins and Wednesday-only credits (no expiry)
  [
    ["dropInSaunaCount", "Sauna Drop-ins", "Drop-in"],
    ["dropInRedLightCount", "Red Light Drop-ins", "Drop-in"],
    ["sharedDropInCount", "Drop-ins", "Drop-in"],
    ["wellnessWednesdayCredits", "Wellness Wednesday", "Wednesday only"],
  ].forEach(([key, name, type]) => {
    const usage = customer[key];
    add(key, name, type, usage, usedUp(usage) ? "Used up" : "Active", "No expiry");
  });

  // Gifts
  [
    ["giftedSaunaCredits", "Gifted Sauna Credits"],
    ["giftedRedLightCredits", "Gifted Red Light Credits"],
    ["sharedGiftedCredits", "Gifted Credits"],
  ].forEach(([key, name]) => {
    const usage = customer[key];
    add(key, name, "Gift", usage, oneTimeStatus(usage, usage?.expiresAt), expiryLabel(usage?.expiresAt));
  });

  // Add-on entitlements from a membership
  [
    ["halotherapyEntitlement", "Halotherapy"],
    ["guestPassEntitlement", "Guest Passes"],
    ["memberGiftSessionEntitlement", "Member Gift Sessions"],
  ].forEach(([key, name]) => {
    const usage = customer[key];
    add(key, name, "Add-on", usage, usedUp(usage) ? "Used up" : "Active");
  });

  return rows;
}

const CreditHistoryTable = ({ customer }) => {
  const rows = buildCreditHistory(customer);
  if (rows.length === 0) {
    return <p className="credit-table__status">No credit history for this customer.</p>;
  }
  return (
    <div className="credit-table__wrap">
      <table className="credit-table credit-table--history" data-testid="credit-history-table">
        <thead>
          <tr>
            <th scope="col">Credit source</th>
            <th scope="col">Type</th>
            <th scope="col">Status</th>
            <th scope="col" className="credit-table__num">Left</th>
            <th scope="col" className="credit-table__num">Booked</th>
            <th scope="col" className="credit-table__num">Completed (period)</th>
            <th scope="col" className="credit-table__num">Total completed</th>
            <th scope="col" className="credit-table__num">Forfeited</th>
            <th scope="col">Dates</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} data-testid={`history-row-${row.key}`}>
              <th scope="row" className="credit-table__source">{row.name}</th>
              <td><span className="credit-table__tag">{row.type}</span></td>
              <td>{row.status}</td>
              <td className="credit-table__num" data-label="Left">{row.left}</td>
              <td className="credit-table__num" data-label="Booked">{row.booked}</td>
              <td className="credit-table__num" data-label="Completed (period)">{row.completed}</td>
              <td className="credit-table__num" data-label="Total completed">{row.totalCompleted}</td>
              <td className="credit-table__num" data-label="Forfeited">{row.forfeited}</td>
              <td className="credit-table__date">{row.dateLabel}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CreditHistoryTable;
